import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  RotateCcw, Plus, Eye, Package, AlertTriangle, Wrench, Scissors,
  X, ChevronLeft, ChevronRight, Search, Upload, Save, CheckCircle2,
  Ban, ImagePlus, FileText, Clock, ArrowRight, FileBox, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  PENDING_INSPECTION: 'bg-amber-100 text-amber-800',
  INSPECTED: 'bg-blue-100 text-blue-800',
  DISPOSITION_IN_PROGRESS: 'bg-purple-100 text-purple-800',
  CLOSED: 'bg-green-100 text-green-800'
};

const statusLabels = {
  PENDING_INSPECTION: 'Pending Inspection',
  INSPECTED: 'Inspected',
  DISPOSITION_IN_PROGRESS: 'In Progress',
  CLOSED: 'Closed'
};

const woStatusColors = {
  PENDING: 'bg-amber-100 text-amber-800',
  PLANNED: 'bg-blue-100 text-blue-800',
  RUNNING: 'bg-indigo-100 text-indigo-800',
  COMPLETED: 'bg-green-100 text-green-800'
};

const CustomerReturns = () => {
  const [returns, setReturns] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Create return dialog
  const [createDialog, setCreateDialog] = useState(false);
  const [newReturn, setNewReturn] = useState({
    customer_id: '', return_date: new Date().toISOString().split('T')[0],
    received_date: '', reference_no: '', dispatch_id: '', invoice_number: '', invoice_date: '', invoice_qty: 0, items: []
  });
  const [newReturnItem, setNewReturnItem] = useState({ item_id: '', return_qty: 1, reason_id: '', remarks: '' });

  // Invoice selection
  const [dispatchDates, setDispatchDates] = useState([]);
  const [selectedDispatchDate, setSelectedDispatchDate] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Detail / Disposition dialog
  const [detailDialog, setDetailDialog] = useState({ open: false, cr: null });
  const [dispositionData, setDispositionData] = useState({});

  // Dismantle dialog
  const [dismantleDialog, setDismantleDialog] = useState({ open: false, returnId: '', itemId: '', qty: 0, children: [] });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  // Loading states for execute actions
  const [executing, setExecuting] = useState({});

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [retRes, custRes, itemRes, reasonRes] = await Promise.all([
        axios.get(`${API}/customer-returns`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/items`),
        axios.get(`${API}/return-reasons`)
      ]);
      setReturns(retRes.data);
      setCustomers(custRes.data);
      setItems(itemRes.data);
      setReasons(reasonRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredReturns = useMemo(() => {
    let result = returns;
    if (statusFilter && statusFilter !== 'all') {
      result = result.filter(r => r.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(r =>
        r.return_number?.toLowerCase().includes(q) ||
        r.customer_name?.toLowerCase().includes(q) ||
        r.reference_no?.toLowerCase().includes(q) ||
        r.invoice_number?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [returns, statusFilter, searchQuery]);

  const paginatedReturns = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredReturns.slice(start, start + pageSize);
  }, [filteredReturns, currentPage]);

  const totalPages = Math.ceil(filteredReturns.length / pageSize);

  const scorecards = useMemo(() => ({
    total: returns.length,
    pending: returns.filter(r => r.status === 'PENDING_INSPECTION').length,
    inProgress: returns.filter(r => r.status === 'DISPOSITION_IN_PROGRESS' || r.status === 'INSPECTED').length,
    closed: returns.filter(r => r.status === 'CLOSED').length
  }), [returns]);

  // ===== CREATE RETURN: Invoice lookup flow =====
  const handleCustomerChange = async (customerId) => {
    setNewReturn(prev => ({ ...prev, customer_id: customerId, dispatch_id: '', invoice_number: '', invoice_date: '', invoice_qty: 0, items: [] }));
    setSelectedDispatchDate('');
    setInvoices([]);
    setSelectedInvoice(null);
    if (!customerId) return;
    try {
      const res = await axios.get(`${API}/customer-returns/dispatch-dates?customer_id=${customerId}`);
      setDispatchDates(res.data);
    } catch { setDispatchDates([]); }
  };

  const handleDispatchDateChange = async (date) => {
    setSelectedDispatchDate(date);
    setSelectedInvoice(null);
    setNewReturn(prev => ({ ...prev, dispatch_id: '', invoice_number: '', invoice_date: '', invoice_qty: 0, items: [] }));
    if (!date || !newReturn.customer_id) return;
    try {
      const res = await axios.get(`${API}/customer-returns/dispatch-invoices?customer_id=${newReturn.customer_id}&dispatch_date=${date}`);
      setInvoices(res.data);
    } catch { setInvoices([]); }
  };

  const handleInvoiceSelect = (invoiceId) => {
    const inv = invoices.find(i => i.dispatch_id === invoiceId);
    if (!inv) return;
    setSelectedInvoice(inv);
    setNewReturn(prev => ({
      ...prev,
      dispatch_id: inv.dispatch_id,
      invoice_number: inv.invoice_number,
      invoice_date: inv.dispatch_date,
      invoice_qty: inv.qty,
      items: [{
        item_id: inv.item_id,
        return_qty: 0,
        part_number: inv.part_number,
        description: inv.description,
        max_qty: inv.qty,
        reason_id: '',
        remarks: ''
      }]
    }));
  };

  const handleReturnQtyChange = (idx, qty) => {
    setNewReturn(prev => {
      const updated = [...prev.items];
      const maxQty = updated[idx].max_qty || Infinity;
      updated[idx] = { ...updated[idx], return_qty: Math.min(qty, maxQty) };
      return { ...prev, items: updated };
    });
  };

  const handleReturnItemReasonChange = (idx, reasonId) => {
    setNewReturn(prev => {
      const updated = [...prev.items];
      updated[idx] = { ...updated[idx], reason_id: reasonId };
      return { ...prev, items: updated };
    });
  };

  const handleReturnItemRemarksChange = (idx, remarks) => {
    setNewReturn(prev => {
      const updated = [...prev.items];
      updated[idx] = { ...updated[idx], remarks };
      return { ...prev, items: updated };
    });
  };

  const removeReturnItem = (idx) => {
    setNewReturn(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const today = new Date().toISOString().split('T')[0];

  const handleCreateReturn = async () => {
    if (!newReturn.customer_id) { toast.error('Select a customer'); return; }
    if (newReturn.items.length === 0) { toast.error('Add at least one return item'); return; }
    if (newReturn.items.some(i => i.return_qty <= 0)) { toast.error('Return qty must be > 0 for all items'); return; }
    if (newReturn.items.some(i => !i.remarks?.trim())) { toast.error('Remarks is mandatory for all return items'); return; }
    if (newReturn.items.some(i => !i.reason_id)) { toast.error('Return reason is mandatory for all items'); return; }
    if (!newReturn.return_date) { toast.error('Return date is required'); return; }
    if (!newReturn.received_date) { toast.error('Received date is required'); return; }
    if (newReturn.return_date > today) { toast.error('Return date cannot be a future date'); return; }
    if (newReturn.received_date > today) { toast.error('Received date cannot be a future date'); return; }
    if (newReturn.invoice_date && newReturn.return_date < newReturn.invoice_date) {
      toast.error(`Return date cannot be before invoice date (${newReturn.invoice_date})`); return;
    }
    if (newReturn.return_date > newReturn.received_date) {
      toast.error('Return date cannot be after received date'); return;
    }

    try {
      const payload = {
        customer_id: newReturn.customer_id,
        return_date: newReturn.return_date,
        received_date: newReturn.received_date,
        reference_no: newReturn.reference_no,
        dispatch_id: newReturn.dispatch_id || undefined,
        invoice_number: newReturn.invoice_number || undefined,
        invoice_date: newReturn.invoice_date || undefined,
        invoice_qty: newReturn.invoice_qty || undefined,
        items: newReturn.items.map(i => ({
          item_id: i.item_id,
          return_qty: i.return_qty,
          reason_id: i.reason_id,
          remarks: i.remarks
        }))
      };
      const res = await axios.post(`${API}/customer-returns`, payload);
      toast.success(`Return ${res.data.return_number} created`);
      resetCreateDialog();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create return');
    }
  };

  const resetCreateDialog = () => {
    setCreateDialog(false);
    setNewReturn({ customer_id: '', return_date: new Date().toISOString().split('T')[0], received_date: '', reference_no: '', dispatch_id: '', invoice_number: '', invoice_date: '', invoice_qty: 0, items: [] });
    setDispatchDates([]);
    setSelectedDispatchDate('');
    setInvoices([]);
    setSelectedInvoice(null);
  };

  // ===== DETAIL / DISPOSITION =====
  const openDetail = async (cr) => {
    try {
      const res = await axios.get(`${API}/customer-returns/${cr.id}`);
      setDetailDialog({ open: true, cr: res.data });
      _initDispositionData(res.data);
    } catch (error) {
      toast.error('Failed to load return details');
    }
  };

  const refreshDetail = async () => {
    const cr = detailDialog.cr;
    if (!cr) return;
    try {
      const res = await axios.get(`${API}/customer-returns/${cr.id}`);
      // Auto-close dialog if return is fully closed
      if (res.data.status === 'CLOSED') {
        setDetailDialog({ open: false, cr: null });
        fetchAll();
        return;
      }
      setDetailDialog({ open: true, cr: res.data });
      _initDispositionData(res.data);
    } catch (error) {
      console.error('Failed to refresh detail');
    }
    fetchAll();
  };

  const _initDispositionData = (crData) => {
    const dData = {};
    for (const item of crData.items) {
      dData[item.item_id] = {
        disposition: item.disposition || 'PENDING',
        reject_qty: item.reject_qty || 0,
        rework_qty: item.rework_qty || 0,
        dismantle_qty: item.dismantle_qty || 0,
        remarks: item.remarks || '',
        reject_executed: item.reject_executed || false,
        rework_executed: item.rework_executed || false,
        dismantle_executed: item.dismantle_executed || false,
        rework_wo_id: item.rework_wo_id || null,
        rework_wo_number: item.rework_wo_number || null,
        rework_wo_status: item.rework_wo_status || null,
        rework_wo_good_qty: item.rework_wo_good_qty || 0,
        rework_wo_scrap_qty: item.rework_wo_scrap_qty || 0,
        dismantle_details: item.dismantle_details || []
      };
    }
    setDispositionData(dData);
  };

  const handleSaveDisposition = async () => {
    const cr = detailDialog.cr;
    if (!cr) return;

    const itemsPayload = cr.items.map(item => {
      const d = dispositionData[item.item_id] || {};
      const totalDisposed = (d.reject_qty || 0) + (d.rework_qty || 0) + (d.dismantle_qty || 0);
      if (totalDisposed !== item.return_qty) {
        toast.error(`Total disposition (${totalDisposed}) must equal return qty (${item.return_qty}) for ${item.part_number}. All units must be accounted for.`);
        return null;
      }
      return {
        item_id: item.item_id,
        disposition: d.disposition || 'PENDING',
        reject_qty: d.reject_qty || 0,
        rework_qty: d.rework_qty || 0,
        dismantle_qty: d.dismantle_qty || 0,
        remarks: d.remarks || ''
      };
    });

    if (itemsPayload.includes(null)) return;

    try {
      await axios.put(`${API}/customer-returns/${cr.id}/disposition`, { items: itemsPayload });
      toast.success('Disposition saved');
      await refreshDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save disposition');
    }
  };

  // Check if an item's disposition is fully saved (total == return_qty)
  const isDispositionSaved = (item) => {
    const d = dispositionData[item.item_id];
    if (!d) return false;
    const total = (d.reject_qty || 0) + (d.rework_qty || 0) + (d.dismantle_qty || 0);
    return total === item.return_qty;
  };

  const handleExecuteReject = async (itemId) => {
    const cr = detailDialog.cr;
    setExecuting(prev => ({ ...prev, [`reject_${itemId}`]: true }));
    try {
      await axios.post(`${API}/customer-returns/${cr.id}/execute-reject`, { item_id: itemId });
      const d = dispositionData[itemId];
      toast.success(`${d?.reject_qty || 0} units rejected and marked as scrap`);
      await refreshDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to execute rejection');
    } finally {
      setExecuting(prev => ({ ...prev, [`reject_${itemId}`]: false }));
    }
  };

  const handleExecuteRework = async (itemId) => {
    const cr = detailDialog.cr;
    setExecuting(prev => ({ ...prev, [`rework_${itemId}`]: true }));
    try {
      const res = await axios.post(`${API}/customer-returns/${cr.id}/execute-rework`, { item_id: itemId });
      toast.success(res.data.message);
      await refreshDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create rework WO');
    } finally {
      setExecuting(prev => ({ ...prev, [`rework_${itemId}`]: false }));
    }
  };

  const openDismantleDialog = async (itemId) => {
    const cr = detailDialog.cr;
    const qty = dispositionData[itemId]?.dismantle_qty || 0;
    if (qty <= 0) { toast.error('No dismantle qty allocated'); return; }
    try {
      const res = await axios.get(`${API}/customer-returns/${cr.id}/bom-children/${itemId}`);
      const children = res.data.map(c => {
        const suggested = Math.round(c.qty_per * qty);
        return {
          ...c,
          suggested_qty: suggested,
          good_qty: suggested,
          scrap_qty: 0
        };
      });
      setDismantleDialog({ open: true, returnId: cr.id, itemId, qty, children });
    } catch (error) {
      toast.error('Failed to load BOM children');
    }
  };

  const handleExecuteDismantle = async () => {
    const { returnId, itemId, qty, children } = dismantleDialog;
    setExecuting(prev => ({ ...prev, [`dismantle_${itemId}`]: true }));
    try {
      await axios.post(`${API}/customer-returns/${returnId}/execute-dismantle`, {
        item_id: itemId, dismantle_qty: qty, child_items: children.map(c => ({
          child_item_id: c.child_item_id,
          good_qty: c.good_qty,
          scrap_qty: c.scrap_qty,
          suggested_qty: c.suggested_qty
        }))
      });
      toast.success('Dismantle completed');
      setDismantleDialog({ open: false, returnId: '', itemId: '', qty: 0, children: [] });
      await refreshDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to execute dismantle');
    } finally {
      setExecuting(prev => ({ ...prev, [`dismantle_${itemId}`]: false }));
    }
  };

  const handleCloseReturn = async () => {
    const cr = detailDialog.cr;
    if (!cr) return;
    try {
      await axios.put(`${API}/customer-returns/${cr.id}/close`);
      toast.success('Return closed');
      setDetailDialog({ open: false, cr: null });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close return');
    }
  };

  const handleImageUpload = async (e) => {
    const cr = detailDialog.cr;
    if (!cr) return;
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/customer-returns/${cr.id}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Image uploaded');
      await refreshDetail();
    } catch (error) { toast.error('Upload failed'); }
    e.target.value = '';
  };

  const getTotalAllocated = (itemId) => {
    const d = dispositionData[itemId];
    if (!d) return 0;
    return (d.reject_qty || 0) + (d.rework_qty || 0) + (d.dismantle_qty || 0);
  };

  const isItemFullyExecuted = (item) => {
    const d = dispositionData[item.item_id];
    if (!d) return false;
    const executedQty =
      (d.reject_executed ? (d.reject_qty || 0) : 0) +
      (d.rework_executed ? (d.rework_qty || 0) : 0) +
      (d.dismantle_executed ? (d.dismantle_qty || 0) : 0);
    return executedQty >= item.return_qty;
  };

  // Check if all items in return are fully allocated + executed (for Close button)
  const canCloseReturn = () => {
    const cr = detailDialog.cr;
    if (!cr) return false;
    return cr.items.every(item => {
      const d = dispositionData[item.item_id];
      if (!d) return false;
      const totalAllocated = (d.reject_qty || 0) + (d.rework_qty || 0) + (d.dismantle_qty || 0);
      if (totalAllocated < item.return_qty) return false;
      return isItemFullyExecuted(item);
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-50 to-orange-50 p-6 rounded-lg border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <RotateCcw className="w-6 h-6 text-orange-600" />
            <div>
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Customer Returns</h2>
              <p className="text-slate-600 text-sm">Manage returns, inspections, and dispositions</p>
            </div>
          </div>
          <Button data-testid="create-return-btn" onClick={() => setCreateDialog(true)} className="bg-orange-600 hover:bg-orange-700">
            <Plus className="w-4 h-4 mr-2" /> New Return
          </Button>
        </div>
      </div>

      {/* Scorecards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Returns', value: scorecards.total, icon: Package, color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Pending Inspection', value: scorecards.pending, icon: AlertTriangle, color: 'text-amber-700', bg: 'bg-amber-50' },
          { label: 'In Progress', value: scorecards.inProgress, icon: Wrench, color: 'text-purple-700', bg: 'bg-purple-50' },
          { label: 'Closed', value: scorecards.closed, icon: CheckCircle2, color: 'text-green-700', bg: 'bg-green-50' },
        ].map(card => (
          <Card key={card.label} className="border shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${card.bg}`}><card.icon className={`w-5 h-5 ${card.color}`} /></div>
              <div><p className="text-2xl font-bold">{card.value}</p><p className="text-xs text-slate-500">{card.label}</p></div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[200px] max-w-sm">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input placeholder="Search by return #, customer, invoice..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9" data-testid="search-returns" />
          </div>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="filter-status"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="PENDING_INSPECTION">Pending Inspection</SelectItem>
            <SelectItem value="INSPECTED">Inspected</SelectItem>
            <SelectItem value="DISPOSITION_IN_PROGRESS">In Progress</SelectItem>
            <SelectItem value="CLOSED">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Returns Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Return #</th>
                  <th className="px-4 py-3 text-left font-semibold">Customer</th>
                  <th className="px-4 py-3 text-left font-semibold">Invoice #</th>
                  <th className="px-4 py-3 text-left font-semibold">Invoice Date</th>
                  <th className="px-4 py-3 text-left font-semibold">Return Date</th>
                  <th className="px-4 py-3 text-left font-semibold">Item</th>
                  <th className="px-4 py-3 text-center font-semibold">Return Qty</th>
                  <th className="px-4 py-3 text-left font-semibold">Status</th>
                  <th className="px-4 py-3 text-left font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedReturns.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500">
                    {loading ? 'Loading...' : 'No customer returns found'}
                  </td></tr>
                ) : paginatedReturns.map(cr => (
                  <tr key={cr.id} className="border-b hover:bg-slate-50" data-testid={`return-row-${cr.id}`}>
                    <td className="px-4 py-3 font-mono font-semibold text-orange-700">{cr.return_number}</td>
                    <td className="px-4 py-3">{cr.customer_name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{cr.invoice_number || cr.reference_no || '-'}</td>
                    <td className="px-4 py-3 text-sm">{cr.invoice_date || '-'}</td>
                    <td className="px-4 py-3 text-sm">{cr.return_date}</td>
                    <td className="px-4 py-3">
                      {cr.items?.map((item, idx) => (
                        <div key={idx} className="text-sm">{item.part_number}</div>
                      ))}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {cr.items?.map((item, idx) => (
                        <div key={idx} className="font-semibold">{item.return_qty}</div>
                      ))}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={statusColors[cr.status] || 'bg-slate-100'}>{statusLabels[cr.status] || cr.status}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant="outline" onClick={() => openDetail(cr)} data-testid={`view-return-${cr.id}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredReturns.length > pageSize && (
            <div className="flex items-center justify-between p-4 border-t">
              <span className="text-sm text-slate-500">Page {currentPage} of {totalPages}</span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== CREATE RETURN DIALOG ===== */}
      <Dialog open={createDialog} onOpenChange={open => { if (!open) resetCreateDialog(); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Customer Return</DialogTitle>
            <DialogDescription>Select customer, invoice date, and invoice to create a return</DialogDescription>
          </DialogHeader>

          {/* Step 1: Customer + Date + Invoice Selection */}
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Customer *</label>
                <Select value={newReturn.customer_id} onValueChange={handleCustomerChange}>
                  <SelectTrigger data-testid="create-return-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Invoice Date *</label>
                <Select value={selectedDispatchDate} onValueChange={handleDispatchDateChange} disabled={!newReturn.customer_id}>
                  <SelectTrigger data-testid="select-dispatch-date"><SelectValue placeholder={dispatchDates.length > 0 ? "Select date" : "Select customer first"} /></SelectTrigger>
                  <SelectContent>
                    {dispatchDates.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Invoice # *</label>
                <Select value={newReturn.dispatch_id} onValueChange={handleInvoiceSelect} disabled={invoices.length === 0}>
                  <SelectTrigger data-testid="select-invoice"><SelectValue placeholder={invoices.length > 0 ? "Select invoice" : "Select date first"} /></SelectTrigger>
                  <SelectContent>
                    {invoices.map(inv => (
                      <SelectItem key={inv.dispatch_id} value={inv.dispatch_id}>
                        {inv.invoice_number} ({inv.part_number} - Qty: {inv.qty})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedInvoice && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <FileBox className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Invoice Details</span>
                </div>
                <div className="grid grid-cols-4 gap-3 text-sm">
                  <div><span className="text-blue-600">Invoice:</span> <strong>{selectedInvoice.invoice_number}</strong></div>
                  <div><span className="text-blue-600">Date:</span> <strong>{selectedInvoice.dispatch_date}</strong></div>
                  <div><span className="text-blue-600">Item:</span> <strong>{selectedInvoice.part_number}</strong></div>
                  <div><span className="text-blue-600">Invoiced Qty:</span> <strong>{selectedInvoice.qty}</strong></div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Return Date *</label>
                <Input type="date" value={newReturn.return_date}
                  min={newReturn.invoice_date || undefined}
                  max={today}
                  onChange={e => setNewReturn(p => ({ ...p, return_date: e.target.value }))} data-testid="create-return-date" />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Received Date *</label>
                <Input type="date" value={newReturn.received_date}
                  min={newReturn.return_date || undefined}
                  max={today}
                  onChange={e => setNewReturn(p => ({ ...p, received_date: e.target.value }))} />
              </div>
            </div>
          </div>

          {/* Return Items (auto-populated from invoice) */}
          {newReturn.items.length > 0 && (
            <div className="border rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-sm flex items-center gap-2"><Package className="w-4 h-4" /> Return Items</h4>
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50"><tr>
                    <th className="px-3 py-2 text-left">Item</th>
                    <th className="px-3 py-2 text-left">Invoiced Qty</th>
                    <th className="px-3 py-2 text-left">Return Qty *</th>
                    <th className="px-3 py-2 text-left">Reason *</th>
                    <th className="px-3 py-2 text-left">Remarks *</th>
                  </tr></thead>
                  <tbody>
                    {newReturn.items.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2 font-medium">{item.part_number} - {item.description}</td>
                        <td className="px-3 py-2 font-semibold text-blue-700">{item.max_qty || '-'}</td>
                        <td className="px-3 py-2">
                          <Input
                            type="number" min="1" max={item.max_qty || 999999}
                            value={item.return_qty}
                            onChange={e => handleReturnQtyChange(idx, parseInt(e.target.value) || 0)}
                            className="w-24"
                            data-testid="add-return-qty"
                          />
                          {item.max_qty && item.return_qty > item.max_qty && (
                            <span className="text-xs text-red-500">Max: {item.max_qty}</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <Select value={item.reason_id} onValueChange={v => handleReturnItemReasonChange(idx, v)}>
                            <SelectTrigger className="w-36"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>{reasons.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2">
                          <Input placeholder="Required" value={item.remarks} onChange={e => handleReturnItemRemarksChange(idx, e.target.value)} className="w-32" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={resetCreateDialog}>Cancel</Button>
            <Button onClick={handleCreateReturn} className="bg-orange-600 hover:bg-orange-700" data-testid="submit-return-btn" disabled={newReturn.items.length === 0}>
              <Save className="w-4 h-4 mr-2" /> Create Return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== DETAIL / DISPOSITION DIALOG ===== */}
      <Dialog open={detailDialog.open} onOpenChange={open => !open && setDetailDialog({ open: false, cr: null })}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          {detailDialog.cr && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-orange-600" />
                      {detailDialog.cr.return_number}
                    </DialogTitle>
                    <DialogDescription>
                      Customer: <strong>{detailDialog.cr.customer_name}</strong> | Date: {detailDialog.cr.return_date}
                      {detailDialog.cr.invoice_number && (<> | Invoice: <strong>{detailDialog.cr.invoice_number}</strong> ({detailDialog.cr.invoice_date})</>)}
                      {!detailDialog.cr.invoice_number && detailDialog.cr.reference_no && (<> | Ref: {detailDialog.cr.reference_no}</>)}
                    </DialogDescription>
                  </div>
                  <Badge className={statusColors[detailDialog.cr.status]}>{statusLabels[detailDialog.cr.status]}</Badge>
                </div>
              </DialogHeader>

              {/* Invoice Info */}
              {detailDialog.cr.invoice_number && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-4 text-sm">
                  <FileBox className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <div className="flex gap-6">
                    <span>Invoice: <strong>{detailDialog.cr.invoice_number}</strong></span>
                    <span>Date: <strong>{detailDialog.cr.invoice_date}</strong></span>
                    {detailDialog.cr.invoice_qty && <span>Invoiced Qty: <strong>{detailDialog.cr.invoice_qty}</strong></span>}
                  </div>
                </div>
              )}

              {/* Evidence Images */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-sm flex items-center gap-2"><ImagePlus className="w-4 h-4" /> Evidence ({detailDialog.cr.images?.length || 0})</h4>
                  {detailDialog.cr.status !== 'CLOSED' && (
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="upload-evidence" />
                      <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border rounded hover:bg-slate-50 cursor-pointer">
                        <Upload className="w-3.5 h-3.5" /> Upload
                      </span>
                    </label>
                  )}
                </div>
                {detailDialog.cr.images?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {detailDialog.cr.images.map((img, i) => (
                      <img key={i} src={`${BACKEND_URL}${img}`} alt={`Evidence ${i + 1}`} className="w-20 h-20 rounded-lg object-cover border cursor-pointer hover:ring-2 hover:ring-orange-400" />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No images uploaded</p>
                )}
              </div>

              {/* Items & Disposition */}
              <div className="space-y-4">
                <h4 className="font-medium">Return Items & Disposition</h4>
                {detailDialog.cr.items.map((item, idx) => {
                  const d = dispositionData[item.item_id] || {};
                  const isClosed = detailDialog.cr.status === 'CLOSED';
                  const totalAllocated = getTotalAllocated(item.item_id);
                  const remaining = item.return_qty - totalAllocated;
                  const fullyExecuted = isItemFullyExecuted(item);

                  return (
                    <Card key={idx} className={`border-2 ${fullyExecuted ? 'border-green-200 bg-green-50/30' : ''}`}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-semibold">{item.part_number}</span>
                            <span className="text-slate-500 ml-2">{item.description}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">Returned: {item.return_qty}</Badge>
                            {totalAllocated > 0 && (
                              <Badge className={remaining === 0 ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                                Allocated: {totalAllocated}/{item.return_qty}
                              </Badge>
                            )}
                          </div>
                        </div>

                        {item.reason_name && (
                          <p className="text-sm text-slate-600">Reason: <strong>{item.reason_name}</strong></p>
                        )}

                        {/* Execution Status Badges */}
                        {(d.reject_executed || d.rework_executed || d.dismantle_executed) && (
                          <div className="flex flex-wrap gap-2">
                            {d.reject_executed && (
                              <Badge className="bg-red-100 text-red-800 gap-1" data-testid={`badge-rejected-${idx}`}>
                                <Ban className="w-3 h-3" /> Rejected: {d.reject_qty} (Scrap)
                              </Badge>
                            )}
                            {d.rework_executed && (
                              <div className="flex items-center gap-1">
                                <Badge className="bg-blue-100 text-blue-800 gap-1" data-testid={`badge-rework-${idx}`}>
                                  <Wrench className="w-3 h-3" /> Rework: {d.rework_wo_number || 'WO Created'} ({d.rework_qty} units)
                                </Badge>
                                {d.rework_wo_status && (
                                  <Badge className={`${woStatusColors[d.rework_wo_status] || 'bg-slate-100'} text-xs gap-1`}>
                                    {d.rework_wo_status}
                                    {d.rework_wo_status === 'COMPLETED' && ` (Good: ${d.rework_wo_good_qty}, Scrap: ${d.rework_wo_scrap_qty})`}
                                  </Badge>
                                )}
                              </div>
                            )}
                            {d.dismantle_executed && (
                              <Badge className="bg-purple-100 text-purple-800 gap-1" data-testid={`badge-dismantled-${idx}`}>
                                <Scissors className="w-3 h-3" /> Dismantled: {d.dismantle_qty}
                              </Badge>
                            )}
                          </div>
                        )}

                        {!fullyExecuted && detailDialog.cr.status !== 'CLOSED' && (
                          <div className="bg-slate-50 rounded-lg p-3 space-y-3">
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Disposition Qty Allocation (Total must equal Return Qty: {item.return_qty})</p>
                              {remaining > 0 && totalAllocated > 0 && (
                                <span className="text-xs text-amber-600 font-medium">Remaining: {remaining}</span>
                              )}
                              {remaining < 0 && (
                                <span className="text-xs text-red-600 font-medium">Over-allocated by {Math.abs(remaining)}!</span>
                              )}
                            </div>
                            {!isDispositionSaved(item) && (
                              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
                                Step 1: Allocate all {item.return_qty} units across Reject/Rework/Dismantle, then click "Save Disposition". Execute buttons will activate after saving.
                              </div>
                            )}
                            <div className="grid grid-cols-3 gap-3">
                              {/* REJECT */}
                              <div>
                                <label className="text-xs font-medium mb-1 block flex items-center gap-1"><Ban className="w-3 h-3 text-red-500" /> Reject (Scrap)</label>
                                <div className="flex gap-1">
                                  <Input type="number" min="0" max={item.return_qty} value={d.reject_qty || 0} disabled={d.reject_executed}
                                    onChange={e => setDispositionData(prev => ({ ...prev, [item.item_id]: { ...prev[item.item_id], reject_qty: parseInt(e.target.value) || 0 } }))}
                                    className="w-20" />
                                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50"
                                    onClick={() => handleExecuteReject(item.item_id)}
                                    disabled={d.reject_executed || !isDispositionSaved(item) || (d.reject_qty || 0) <= 0 || executing[`reject_${item.item_id}`]}
                                    data-testid={`reject-btn-${idx}`}>
                                    {d.reject_executed ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Done</> :
                                     executing[`reject_${item.item_id}`] ? <><Clock className="w-3.5 h-3.5 mr-1 animate-spin" /> ...</> :
                                     <><Ban className="w-3.5 h-3.5 mr-1" /> Execute</>}
                                  </Button>
                                </div>
                              </div>
                              {/* REWORK */}
                              <div>
                                <label className="text-xs font-medium mb-1 block flex items-center gap-1"><Wrench className="w-3 h-3 text-blue-500" /> Rework (WO)</label>
                                <div className="flex gap-1">
                                  <Input type="number" min="0" max={item.return_qty} value={d.rework_qty || 0} disabled={d.rework_executed}
                                    onChange={e => setDispositionData(prev => ({ ...prev, [item.item_id]: { ...prev[item.item_id], rework_qty: parseInt(e.target.value) || 0 } }))}
                                    className="w-20" />
                                  <Button size="sm" variant="outline" className="text-blue-600 hover:bg-blue-50"
                                    onClick={() => handleExecuteRework(item.item_id)}
                                    disabled={d.rework_executed || !isDispositionSaved(item) || (d.rework_qty || 0) <= 0 || executing[`rework_${item.item_id}`]}
                                    data-testid={`rework-btn-${idx}`}>
                                    {d.rework_executed ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> WO Created</> :
                                     executing[`rework_${item.item_id}`] ? <><Clock className="w-3.5 h-3.5 mr-1 animate-spin" /> ...</> :
                                     <><Wrench className="w-3.5 h-3.5 mr-1" /> Create WO</>}
                                  </Button>
                                </div>
                              </div>
                              {/* DISMANTLE */}
                              <div>
                                <label className="text-xs font-medium mb-1 block flex items-center gap-1"><Scissors className="w-3 h-3 text-purple-500" /> Dismantle</label>
                                <div className="flex gap-1">
                                  <Input type="number" min="0" max={item.return_qty} value={d.dismantle_qty || 0} disabled={d.dismantle_executed}
                                    onChange={e => setDispositionData(prev => ({ ...prev, [item.item_id]: { ...prev[item.item_id], dismantle_qty: parseInt(e.target.value) || 0 } }))}
                                    className="w-20" />
                                  <Button size="sm" variant="outline" className="text-purple-600 hover:bg-purple-50"
                                    onClick={() => openDismantleDialog(item.item_id)}
                                    disabled={d.dismantle_executed || !isDispositionSaved(item) || (d.dismantle_qty || 0) <= 0 || executing[`dismantle_${item.item_id}`]}
                                    data-testid={`dismantle-btn-${idx}`}>
                                    {d.dismantle_executed ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Done</> :
                                     <><Scissors className="w-3.5 h-3.5 mr-1" /> Dismantle</>}
                                  </Button>
                                </div>
                              </div>
                            </div>
                            <div>
                              <label className="text-xs font-medium mb-1 block">Remarks *</label>
                              <Input placeholder="Remarks required" value={d.remarks || ''}
                                onChange={e => setDispositionData(prev => ({ ...prev, [item.item_id]: { ...prev[item.item_id], remarks: e.target.value } }))}
                                className={!d.remarks?.trim() ? 'border-red-300' : ''} />
                              {!d.remarks?.trim() && <span className="text-xs text-red-500">Required</span>}
                            </div>
                          </div>
                        )}

                        {/* Rework WO Tracking */}
                        {d.rework_executed && d.rework_wo_number && (
                          <div className="bg-blue-50 rounded-lg p-3 mt-2">
                            <p className="text-xs font-medium text-blue-700 mb-2 uppercase tracking-wide">Rework Work Order Tracking</p>
                            <div className="flex items-center gap-4 text-sm">
                              <span>WO: <strong>{d.rework_wo_number}</strong></span>
                              <Badge className={woStatusColors[d.rework_wo_status] || 'bg-slate-100'}>{d.rework_wo_status || 'PENDING'}</Badge>
                              <span>Plan: {d.rework_qty}</span>
                              {d.rework_wo_status === 'COMPLETED' && (
                                <>
                                  <span className="text-green-700">Good: {d.rework_wo_good_qty}</span>
                                  <span className="text-red-600">Scrap: {d.rework_wo_scrap_qty}</span>
                                </>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Dismantle Breakdown */}
                        {d.dismantle_executed && d.dismantle_details?.length > 0 && (
                          <div className="bg-purple-50 rounded-lg p-3 mt-2">
                            <p className="text-xs font-medium text-purple-700 mb-2 uppercase tracking-wide">Dismantle Breakdown</p>
                            <div className="space-y-1">
                              {d.dismantle_details.map((child, ci) => {
                                const childItem = items.find(i => i.id === child.child_item_id);
                                return (
                                  <div key={ci} className="flex items-center gap-2 text-xs">
                                    <ArrowRight className="w-3 h-3 text-purple-400" />
                                    <span className="font-medium">{childItem?.part_number || child.child_item_id}</span>
                                    <span className="text-slate-500">{childItem?.description || ''}</span>
                                    <div className="flex gap-2 ml-auto">
                                      {(child.good_qty > 0) && (
                                        <Badge className="bg-green-100 text-green-700 text-xs">Inventory: {child.good_qty}</Badge>
                                      )}
                                      {(child.scrap_qty > 0) && (
                                        <Badge className="bg-red-100 text-red-700 text-xs">Scrap: {child.scrap_qty}</Badge>
                                      )}
                                      {/* Backward compat for old format */}
                                      {child.qty_recovered !== undefined && child.good_qty === undefined && (
                                        <>
                                          <Badge variant="outline" className="text-xs">{child.qty_recovered} units</Badge>
                                          <Badge className={child.action === 'INVENTORY' ? 'bg-green-100 text-green-700 text-xs' : 'bg-red-100 text-red-700 text-xs'}>
                                            {child.action === 'INVENTORY' ? 'Inventory' : 'Scrap'}
                                          </Badge>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <DialogFooter className="gap-2">
                {detailDialog.cr.status !== 'CLOSED' && (
                  <>
                    <Button variant="outline" onClick={handleSaveDisposition} data-testid="save-disposition-btn">
                      <Save className="w-4 h-4 mr-2" /> Save Disposition
                    </Button>
                    <Button onClick={handleCloseReturn}
                      className={canCloseReturn() ? "bg-green-600 hover:bg-green-700" : "bg-slate-400 cursor-not-allowed"}
                      disabled={!canCloseReturn()}
                      data-testid="close-return-btn"
                      title={!canCloseReturn() ? "All items must be fully allocated and executed before closing" : "Close this return"}>
                      <CheckCircle2 className="w-4 h-4 mr-2" /> Close Return
                    </Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== DISMANTLE DIALOG ===== */}
      <Dialog open={dismantleDialog.open} onOpenChange={open => !open && setDismantleDialog({ open: false, returnId: '', itemId: '', qty: 0, children: [] })}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scissors className="w-5 h-5 text-purple-600" />
              Dismantle - BOM Breakdown
            </DialogTitle>
            <DialogDescription>
              Dismantling {dismantleDialog.qty} units. For each child, Good Qty goes to inventory, Scrap Qty is written off. Good + Scrap must equal Suggested.
            </DialogDescription>
          </DialogHeader>

          {dismantleDialog.children.length === 0 ? (
            <div className="py-8 text-center text-slate-500">No BOM found for this item. Cannot dismantle.</div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Part #</th>
                    <th className="px-3 py-2 text-left">Description</th>
                    <th className="px-3 py-2 text-left">Category</th>
                    <th className="px-3 py-2 text-center">BOM Qty/Unit</th>
                    <th className="px-3 py-2 text-center">Suggested</th>
                    <th className="px-3 py-2 text-center">Good Qty (Inventory)</th>
                    <th className="px-3 py-2 text-center">Scrap Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {dismantleDialog.children.map((child, idx) => {
                    const suggested = child.suggested_qty;
                    const total = (child.good_qty || 0) + (child.scrap_qty || 0);
                    const overLimit = total !== suggested;
                    return (
                      <tr key={idx} className={`border-t ${overLimit ? 'bg-red-50' : ''}`}>
                        <td className="px-3 py-2 font-medium">{child.part_number}</td>
                        <td className="px-3 py-2 text-slate-600">{child.description}</td>
                        <td className="px-3 py-2"><Badge variant="outline" className="text-xs">{child.category}</Badge></td>
                        <td className="px-3 py-2 text-center">{child.qty_per}</td>
                        <td className="px-3 py-2 text-center font-semibold">{suggested}</td>
                        <td className="px-3 py-2 text-center">
                          <Input type="number" min="0" max={suggested} className="w-20 mx-auto text-center border-green-300" value={child.good_qty}
                            onChange={e => {
                              const val = parseInt(e.target.value) || 0;
                              const newChildren = [...dismantleDialog.children];
                              newChildren[idx] = { ...newChildren[idx], good_qty: val };
                              setDismantleDialog(prev => ({ ...prev, children: newChildren }));
                            }}
                            data-testid={`dismantle-good-${idx}`} />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Input type="number" min="0" max={suggested} className="w-20 mx-auto text-center border-red-300" value={child.scrap_qty}
                            onChange={e => {
                              const val = parseInt(e.target.value) || 0;
                              const newChildren = [...dismantleDialog.children];
                              newChildren[idx] = { ...newChildren[idx], scrap_qty: val };
                              setDismantleDialog(prev => ({ ...prev, children: newChildren }));
                            }}
                            data-testid={`dismantle-scrap-${idx}`} />
                          {overLimit && <span className="text-xs text-red-600 block mt-0.5">Exceeds max {suggested}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDismantleDialog({ open: false, returnId: '', itemId: '', qty: 0, children: [] })}>Cancel</Button>
            <Button onClick={handleExecuteDismantle} className="bg-purple-600 hover:bg-purple-700" data-testid="execute-dismantle-btn"
              disabled={dismantleDialog.children.length === 0 || dismantleDialog.children.some(c => (c.good_qty || 0) + (c.scrap_qty || 0) !== c.suggested_qty)}>
              <Scissors className="w-4 h-4 mr-2" /> Execute Dismantle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerReturns;
